<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges"></alert-unsaved-changes>
    <v-progress-linear
      v-show="loading || providerLoading"
      color="neutral400"
      indeterminate
      rounded
      height="3"
    ></v-progress-linear>
    <v-form
      v-show="!loading && !providerLoading"
      ref="form"
      v-model="valid"
      :lazy-validation="false"
    >
      <v-row>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('provider-name') }}</label>
          <p class="label-info">{{ $t('provider-name-message') }}</p>
          <v-text-field
            v-model.trim="provider.name"
            outlined
            :placeholder="$t('provider-name')"
            :rules="minMaxLengthRule(1, 100)"
            class="required"
            data-cy="name"
            :disabled="!edit"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('hosting-institution') }}</label>
          <p class="label-info">{{ $t('hosting-institution-message') }}</p>
          <v-autocomplete
            v-model="provider.hostingInstitution"
            :items="institutions"
            :item-value="'id'"
            :item-text="'name'"
            return-object
            :placeholder="$t('hosting-institution')"
            outlined
            :disabled="!edit"
            :rules="requiredRule()"
            class="required"
            data-cy="hostingInstitution"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('website') }}</label>
          <p class="label-info">{{ $t('website-message') }}</p>
          <v-text-field
            v-model="provider.website"
            outlined
            :placeholder="$t('website')"
            :rules="requiredRule() && checkURL()"
            class="required"
            :disabled="!edit"
            data-cy="website"
            prepend-inner-icon="mdi-link-variant"
          ></v-text-field>
        </v-col>
        <v-col cols="12" v-if="!next">
          <v-expansion-panels>
            <v-expansion-panel :key="1">
              <v-expansion-panel-header>
                {{ $t('optional-info') }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="pt-10">
                <v-row>
                  <v-col cols="12" sm="12" md="6">
                    <label>{{ $t('abbreviation') }}</label>
                    <p class="label-info">{{ $t('abbreviation-message') }}</p>
                    <v-text-field
                      v-model="provider.abbreviation"
                      outlined
                      clearable
                      :disabled="!edit"
                      :placeholder="$t('abbreviation')"
                      :rules="maxLengthRule(30)"
                      data-cy="abbreviation"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-form>
    <!--BUTTONS-->
    <v-row class="mt-10" v-if="next">
      <v-col cols="6" offset="6">
        <v-btn
          depressed
          rounded
          x-large
          color="primary200"
          @click="add()"
          :disabled="!valid"
          class="white--text font-weight-bold"
          data-cy="submit"
          >{{ $t('next') }}
          <v-icon color="white">$vuetify.icons.next</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row class="mt-10" v-if="!next && edit">
      <v-col cols="6" class="d-flex justify-end" v-if="!admin">
        <v-btn
          depressed
          rounded
          x-large
          color="black"
          @click="cancel()"
          outlined
          class="black--text font-weight-bold"
          data-cy="submit"
        >
          {{ $t('cancel') }}
        </v-btn>
      </v-col>
      <v-col :cols="admin ? '12' : '6'" :class="admin ? 'text-center' : ''">
        <v-btn
          depressed
          rounded
          x-large
          color="neutral"
          @click="add()"
          :disabled="!valid"
          class="white--text font-weight-bold"
          data-cy="submit"
          >{{ $t('update') }}
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import EventBus from '@/helpers/event-bus.js';
import util from '@/mixins/util.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';

export default {
  name: 'ProviderBasicInfo',
  mixins: [util],
  props: {
    admin: {
      type: Boolean,
      default: false,
    },
    edit: {
      type: Boolean,
      default: true,
    },
    next: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    AlertUnsavedChanges,
  },
  data() {
    return {
      valid: false,
      loadingComponents: -1,
      unsavedChanges: false,
      provider: {
        id: null,
        name: null,
        abbreviation: null,
        website: null,
        hostingInstitution: null,
      },
      institutions: [],
      providerLoading: true,
    };
  },
  mounted() {
    this.axios
      .get('/api/v1/hosting-institutions/all')
      .then(res => (this.institutions = res.data))
      .finally(() => {
        this.loadingComponents++;
      });
  },
  methods: {
    cancel() {
      this.$emit('cancel', true);
    },
    add() {
      EventBus.$emit('waiting', true);
      this.axios
        .post('/api/v1/provider-settings/basic-info', this.provider)
        .then(res => {
          console.log('Success');
          sessionStorage.setItem('providerId', res.data.id);
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('provider-updated'),
          });
          this.unsavedChanges = false;
          this.$emit('tab', 1);
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    getProvider(id) {
      EventBus.$emit('waiting', true);
      this.axios
        .get('/api/v1/provider-settings/' + id)
        .then(res => {
          this.provider = res.data;
          this.$nextTick(() => {
            this.providerLoading = false;
          });
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
  },
  computed: {
    loading() {
      return this.loadingComponents !== 0;
    },
  },
  watch: {
    loading(value) {
      let id = this.$route.params.id;
      let newProvider = this.$route.params.new;
      if (value === false) {
        if (sessionStorage.getItem('providerId') && !newProvider) {
          this.getProvider(sessionStorage.getItem('providerId'));
        } else if (id != null) {
          this.getProvider(id);
        } else {
          this.$nextTick(() => {
            this.providerLoading = false;
          });
        }
      }
    },
    provider: {
      handler: function(val, oldVal) {
        console.log('[Provider] Value changed');
        if (oldVal.id) {
          this.unsavedChanges = true;
        }
      },
      deep: true,
    },
  },
};
</script>
